/**
 * Pure reducers for cross-room voice occupancy ("N en la voz de #canal" badges).
 *
 * Three backend WS events feed this and can race, so the convergence rules live
 * here as pure `map → map` transforms (testable without Zustand or the socket):
 *   - voice:channel_occupancy → applyOccupancy (full snapshot, replaces roster)
 *   - voice:participant_joined → applyJoined  (incremental add, dedupe by user)
 *   - voice:participant_left   → applyLeft    (incremental remove, no-op if absent)
 *
 * The store (`useVoicePresenceStore`) is a thin wrapper that calls these in
 * `set(...)`. Count for a channel is simply `map[channelId]?.length ?? 0`.
 */
export interface VoiceParticipant {
  userId: string;
  name?: string;
}

/** channelId → participants currently in that voice room */
export type VoiceOccupancyMap = Record<string, VoiceParticipant[]>;

function dedupe(participants: VoiceParticipant[]): VoiceParticipant[] {
  const seen = new Set<string>();
  const out: VoiceParticipant[] = [];
  for (const p of participants) {
    if (!p.userId || seen.has(p.userId)) continue;
    seen.add(p.userId);
    out.push(p);
  }
  return out;
}

export function applyOccupancy(
  map: VoiceOccupancyMap,
  channelId: string,
  participants: VoiceParticipant[],
): VoiceOccupancyMap {
  return { ...map, [channelId]: dedupe(participants) };
}

export function applyJoined(
  map: VoiceOccupancyMap,
  channelId: string,
  participant: VoiceParticipant,
): VoiceOccupancyMap {
  const current = map[channelId] ?? [];
  if (current.some((p) => p.userId === participant.userId)) return map;
  return { ...map, [channelId]: [...current, participant] };
}

export function applyLeft(
  map: VoiceOccupancyMap,
  channelId: string,
  userId: string,
): VoiceOccupancyMap {
  const current = map[channelId];
  if (!current || !current.some((p) => p.userId === userId)) return map;
  return { ...map, [channelId]: current.filter((p) => p.userId !== userId) };
}

/**
 * Drop a channel's key entirely — used when the channel is DELETED (or the
 * current user loses access), so a stale roster can't keep a "N en la voz"
 * badge alive for a room that no longer exists. No-op (same reference) when
 * the key isn't present.
 */
export function applyChannelPruned(
  map: VoiceOccupancyMap,
  channelId: string,
): VoiceOccupancyMap {
  if (!(channelId in map)) return map;
  const { [channelId]: _pruned, ...rest } = map;
  return rest;
}
